import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { NavLink, useNavigate } from "react-router-dom";
import { CAMPAIGN_BREADCRUMB } from "../../components/apps/campaigns/constants";
import LineNameLabel from "../../components/common/LineNameLabel";
import {
  CampaignTable,
  CampaignModal,
  DeleteCampaign,
  LaunchCampaignModal,
  EditCampaignModal,
} from "../../components/apps/campaigns";
import {
  getCampaigns,
  normalizeCampaign,
  deleteCampaign,
  updateCampaign,
  launchCampaign,
  getCampaignBalance,
} from "../../actions/campaigns/campaigns";
import { useSelector } from "react-redux";
import { formatFilterDate, addDays, isProgrammingDateExpired } from "../../utils/dateFormatters-campaign.utils";

export const CampaignsPage = () => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openLaunchModal, setOpenLaunchModal] = useState(false);

  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [campaignToLaunch, setCampaignToLaunch] = useState(null);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const [cloneInitialData, setCloneInitialData] = useState(null);
  const cloneKeyRef = useRef(0);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

const [searchTerm, setSearchTerm] = useState('');
const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
const [statusFilter, setStatusFilter] = useState('todos');
const [dateRange, setDateRange] = useState({ from: null, to: null }); 

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const searchTimerRef = useRef(null);

  const { name: username, gupshup_app_id: apiKey, id_customer } = useSelector(
    (state) => state.agent
  );

  useEffect(() => {
    if (username && apiKey) {
      loadCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter, dateRange, debouncedSearchTerm, username, apiKey]);

  useEffect(() => {
    if (!username || !apiKey) return;

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      // Resetear a página 1 y aplicar la búsqueda en el mismo tick para una sola request
      setPage(1);
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const loadCampaigns = async () => {
    setLoading(true);

    try {
      let dateFrom = null;
    let dateTo = null;

    if (dateRange.from) {
      dateFrom = formatFilterDate(dateRange.from);
    }

    if (dateRange.to) {
      // Agregar 1 día para incluir el día completo en el filtro
      dateTo = formatFilterDate(addDays(dateRange.to, 1));
    }

      const result = await getCampaigns({
        username,
        apiKey,
        page,
        pageSize,
        name_or_alias: debouncedSearchTerm,
        status: statusFilter,
        dateFrom,
        dateTo,
      });

      const normalizedCampaigns = result.campaigns.map(normalizeCampaign);

      setCampaigns(normalizedCampaigns);
      setTotal(result.total);
    } catch (err) {
      console.error("Error al cargar campañas:", err);
      setCampaigns([]);
      showSnackbar(
        "Error al cargar las campañas. Por favor, intenta de nuevo.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleOpenModal = () => setOpenModal(true);

  const handleCloseModal = () => {
    setOpenModal(false);
    setCloneInitialData(null);
  };

  const handleCampaignCreated = () => {
    setOpenModal(false);
    setCloneInitialData(null);
    loadCampaigns();
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleDateRangeChange = (newDateRange) => {
  setDateRange(newDateRange);
  setPage(1);
};

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleViewCampaign = (campaign) => {
    navigate(`/admin/campaigns/${campaign.id}`);
  };

  const handleEditCampaign = (campaign) => {
    setCampaignToEdit(campaign);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setCampaignToEdit(null);
  };

  const handleSaveEdit = async (payload) => {
    if (!campaignToEdit) return;

    try {
      await updateCampaign({
        campaignId: campaignToEdit.id,
        username,
        apiKey,
        data: payload,
      });

      showSnackbar("Campaña actualizada exitosamente", "success");
      handleCloseEditModal();
      loadCampaigns();
    } catch (error) {
      console.error("Error al actualizar campaña:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Error al actualizar la campaña";
      showSnackbar(message, "error");
      throw error; 
    }
  };

  const handleDeleteCampaign = (campaign) => {
    setCampaignToDelete(campaign);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setCampaignToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!campaignToDelete) return;

    try {
      await deleteCampaign({
        campaignId: campaignToDelete.id,
        username,
        apiKey,
      });

      showSnackbar("Campaña eliminada exitosamente", "success");
      loadCampaigns();
    } catch (error) {
      console.error("Error al eliminar campaña:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Error al eliminar la campaña";
      showSnackbar(message, "error");
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleLaunchCampaign = (campaign) => {
    setCampaignToLaunch(campaign);
    setOpenLaunchModal(true);
  };

  const handleCloseLaunchModal = () => {
    setOpenLaunchModal(false);
    setCampaignToLaunch(null);
  };

  const handleConfirmLaunch = async () => {
    if (!campaignToLaunch) return;

    try {
      // Validar que la fecha programada no esté vencida en zona horaria de Mexico
      if (campaignToLaunch.fechaProgramacion &&
          isProgrammingDateExpired(campaignToLaunch.fechaProgramacion)) {
        setErrorDialogMessage(
          "La fecha programada de esta campaña ya pasó en la zona horaria de México. No se puede lanzar una campaña vencida."
        );
        setErrorDialogOpen(true);
        return;
      }

      // Validar saldo antes de lanzar
      const balance = await getCampaignBalance({ username, apiKey });

      if (!balance.campaigns_enabled || balance.available_credit <= 0) {
        showSnackbar(
          `Saldo insuficiente para lanzar la campaña. Crédito disponible: $${balance.available_credit?.toFixed(2) || '0.00'} USD. Realiza una recarga para continuar.`,
          "error"
        );
        handleCloseLaunchModal();
        return;
      }

      await launchCampaign({
        campaignId: campaignToLaunch.id,
        username,
        apiKey,
      });

      showSnackbar(
        "Campaña lanzada exitosamente. Se está ejecutando.",
        "success"
      );
      handleCloseLaunchModal();
      loadCampaigns();
    } catch (error) {
      console.error("Error al lanzar campaña:", error);
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Error al lanzar la campaña";

      // Si el error es por templates no aprobados, mostrar diálogo con opción a editar
      if (message.toLowerCase().includes("template")) {
        handleCloseLaunchModal();
        setErrorDialogMessage(message);
        setErrorDialogOpen(true);
        return;
      }

      showSnackbar(message, "error");
      throw error;
    }
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    handleCloseLaunchModal();
  };

  const handleEditFromErrorDialog = () => {
    setErrorDialogOpen(false);
    handleCloseLaunchModal();
    handleEditCampaign(campaignToLaunch);
  };

  const handleCloneCampaign = (campaign) => {
    const initialData = {
      campaignName: `${campaign.nombre} (Copia)`,
      alias: campaign.alias ? `${campaign.alias}_copia` : '',
      template: campaign.template,
      descripcion: campaign.descripcion || '',
      audienceType: campaign.tipoContactos || '',
      selectedTemplate: null,
      selectedContacts: [],
      uploadedFile: null,
    };

    cloneKeyRef.current += 1;
    setCloneInitialData(initialData);
    setOpenModal(true);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        px: 1,
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 4,
            flex: "0 0 auto",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
              <LineNameLabel/>
            </Typography>

            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              {CAMPAIGN_BREADCRUMB.map((item) => (
                <Box key={item.title}>
                  {item.to ? (
                    <Link
                      underline="none"
                      color="inherit"
                      component={NavLink}
                      to={item.to}
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <Typography color="textPrimary">{item.title}</Typography>
                  )}
                </Box>
              ))}
            </Breadcrumbs>
          </Box>

          <Button
            sx={{ mt: 2 }}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            Crear Campaña
          </Button>
        </Box>

        <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
          <CampaignTable
            campaigns={campaigns}
            loading={loading}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            dateRange={ dateRange }
            onDateRangeChange={handleDateRangeChange}
            onSearchChange={handleSearchChange}
            onStatusFilterChange={handleStatusFilterChange}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onViewCampaign={handleViewCampaign}
            onEditCampaign={handleEditCampaign}
            onDeleteCampaign={handleDeleteCampaign}
            onLaunchCampaign={handleLaunchCampaign}
            onCloneCampaign={handleCloneCampaign}
          />
        </Box>

        <CampaignModal
          key={cloneKeyRef.current || undefined}
          open={openModal}
          onClose={handleCloseModal}
          onSubmit={handleCampaignCreated}
          initialData={cloneInitialData}
        />

        <DeleteCampaign
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          campaign={campaignToDelete}
        />
        <EditCampaignModal
          open={openEditModal}
          onClose={handleCloseEditModal}
          campaign={campaignToEdit}
          onSave={handleSaveEdit}
        />

        <LaunchCampaignModal
          open={openLaunchModal}
          onClose={handleCloseLaunchModal}
          campaign={campaignToLaunch}
          onConfirm={handleConfirmLaunch}
        />
      </Box>

      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main", fontWeight: 700 }}>
          {errorDialogMessage.toLowerCase().includes("template")
            ? "Templates no aprobados"
            : "Campaña vencida"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.primary">
            {errorDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditFromErrorDialog} variant="outlined" color="primary">
            Editar campaña
          </Button>
          <Button onClick={handleCloseErrorDialog} variant="contained" color="primary">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
